import * as React from 'react';
import { Box, Typography } from '@mui/material';
import styled from 'styled-components';
import moment from 'moment';
import Linkify from 'react-linkify';
import PostbackButton from "../PostBackButtons/PostBackButtons";
import RichMediaUpload from "../RichMediaUpload/RichMediaUpload";
import DOBInputSeparate from "../RichMediaDOB/RichMediaDOB";
import UsernameInput from "../RichmediaUsername/RichmediaUsername";
import { useSelector } from 'react-redux';
import { selectConfig } from '../../redux/slices/configSlice';

const EventText = styled.div`
    font-size: 11px;
    color: #7e7e7e;
    font-style: italic;
    display: initial;
    padding-left: 10px;
    padding-right: 10px;
    background: #fff;
`;

const EventTime = styled.div`
    font-size: 11px;
    color: #7e7e7e;
    font-style: italic;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
`;

interface RichMediaPayload {
    actions?: { text: string; payload: string }[];
}
interface ElementText {
    readonly text: string;
    readonly textFormat?: string;
}

interface MessageBody {
    readonly elementText: ElementText;
    readonly richMediaPayload?: any;
    readonly elementType: string;
}

interface MessageProps {
    readonly message: {
        readonly senderParticipantName: string;
        readonly senderParticipantType: string;
        readonly senderType?: string;
        readonly body: MessageBody;
        readonly receivedAt: string;
        readonly lastUpdatedTimestamp?: string;
        readonly messageId?: string;
    };
    readonly previousMessageType?: "RICH_MEDIA" | "NORMAL";
    readonly prevRichMediaId?: string;
    readonly isThisRichMediaReplied?: boolean;
}


export default function Message({ 
    message, 
    previousMessageType = "NORMAL", 
    prevRichMediaId = "", 
    isThisRichMediaReplied = false 
}: MessageProps) {
    const [replied, setReplied] = React.useState(false);

    const config = useSelector(selectConfig);
    const formData = config?.data?.[0]?.attributes;

    const handlePostback = (payload: string) => {
        setReplied(true);
    };



    const isUserMessage = message?.senderParticipantType === 'CUSTOMER';
    const isEventMessage = (senderType: string) =>
        senderType === "PARTICIPANT_ADDED" || senderType === "PARTICIPANT_REMOVED";

    // Use empty string if senderType is undefined
    const isEvent = isEventMessage(message.senderType ?? "");



    const getTimestamp = () => isEvent ? message.lastUpdatedTimestamp : new Date(message.receivedAt).getTime();
    const timeStamp = getTimestamp();


    const dateString = moment(timeStamp).format("MM/DD/YYYY, H:mm:ss");
    const timeString = moment(timeStamp).format("H:mm:ss");

    const messageStyles = {
        background: isUserMessage ? "#5E5E5E" : "#D9D9D9",
        color: isUserMessage ? "#fff" : "#000",
        borderRadius: isUserMessage
          ? "15px 0px 15px 15px"
          : "0px 15px 15px 15px",
        marginLeft: isUserMessage ? "0" : "35px",
      };
      

    const hasRichMedia = Boolean(message.body.richMediaPayload);
    const isSystemSender = message.senderParticipantType === "SYSTEM";
    const isCustomerSender = message.senderParticipantType === "CUSTOMER";
    const elementType = message.body.elementType;

    const isRichMedia = hasRichMedia;
    const isReply = elementType === 'reply';
    const isImageElement = elementType === 'image';

    const isRichMediaMessage = isRichMedia && isSystemSender;
    const isRichMediaReply = isRichMedia && isCustomerSender && isReply;
    
    // New custom check for rich media reply based on previous message type
    const isRichMediaReplyCustom = (message.senderParticipantType === "CUSTOMER" && 
                                   message.body.elementText && 
                                   message.body.elementText.text && 
                                   previousMessageType === "RICH_MEDIA") ? true : false;

    const isImage = isRichMediaMessage && isImageElement;
    const isChip = isRichMediaMessage && (message.body.elementType == 'chips');
    let isDOB = isRichMediaMessage && (message.body.elementType == 'inputDate');
    let isInputText = isRichMediaMessage && (message.body.elementType == 'inputText');

    const actionList = isRichMediaMessage ? message.body.richMediaPayload.actions : [];


    React.useEffect(() => {
        if ((isRichMediaReply || isRichMediaReplyCustom || isThisRichMediaReplied) && replied !== true) {
            console.log("Setting replied to true for message:", message);
            setReplied(true);
            
            // Notify parent components that a reply has been made
            // This could be done through a callback prop if needed
        }
    }, [isRichMediaReply, isRichMediaReplyCustom, replied, message, isThisRichMediaReplied]);

    // Add this effect to disable all rich media buttons when a customer replies
    React.useEffect(() => {
        if (isRichMediaMessage && message.messageId && isThisRichMediaReplied) {
            // Only disable buttons for this specific rich media message
            const messageElement = document.querySelector(`[data-message-id="${message.messageId}"]`);
            if (messageElement) {
                const buttons = messageElement.getElementsByClassName('dafabet');
                Array.from(buttons).forEach(button => {
                    button.setAttribute('disabled', 'true');
                    if (button instanceof HTMLElement) {
                        button.style.backgroundColor = 'lightgray';
                        button.style.color = 'darkgray';
                        button.style.cursor = 'not-allowed';
                    }
                });
            }
        }
    }, [isRichMediaMessage, message.messageId, isThisRichMediaReplied]);

    return (
        <Box
            data-message-id={message.messageId || ""}
            sx={{
                display: 'flex',
                alignItems: isUserMessage ? 'flex-end' : 'flex-start',
                padding: "4px 8px",
                position: 'relative',
                flexDirection: "column"
            }}
        >
            {/* !isUserMessage && !isEvent && (
                <Box
                    sx={{
                        background: isUserMessage ? "#5E5E5E" : "#D9D9D9",
                        color: isUserMessage ? "#fff" : "#000",
                        borderRadius: isUserMessage ? "15px 0px 15px 15px" : "0px 15px 15px 15px",
                        padding: "10px 15px",
                        marginLeft: isUserMessage ? "0" : "35px",
                        fontSize: "14px",
                        maxWidth: "80%",
                        position: 'relative',
                    }}
                >
                    {!isUserMessage && message?.senderParticipantName && (

                        <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                            {message?.senderParticipantName} {timeString}
                        </Typography>
                    )}
                    {message?.body?.elementText?.textFormat === 'PLAINTEXT' ? (
                        <Typography><Linkify>{message?.body?.elementText?.text}</Linkify></Typography>
                    ) : (
                        <Typography dangerouslySetInnerHTML={{ __html: message?.body?.elementText?.text }} />
                    )}
                    {isUserMessage && (
                        <Typography sx={{ fontSize: "10px", color: "#ccc", marginTop: "4px" }}>{timeString}</Typography>
                    )}
                </Box>
            )**/}
            {isEvent ? (
                <Box
                    sx={{
                        padding: "10px",
                        borderRadius: "8px",
                        textAlign: "center",
                        width: "100%",
                    }}
                >
                    <Typography variant="body2" sx={{ color: "#333" }}>
                        {message.body.elementText.text}
                        {message?.senderType === "PARTICIPANT_ADDED" &&
                            ` ${formData?.ChatBox?.JoinChatMessage || ""}`}
                        {message?.senderType === "PARTICIPANT_REMOVED" &&
                            ` ${formData?.ChatBox?.LeaveChatMessage || ""}`}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                        {dateString}
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        ...messageStyles,
                        padding: "10px 15px",
                        fontSize: "14px",
                        maxWidth: "80%",
                        position: "relative"
                    }}
                >
                    {!isUserMessage && message?.senderParticipantName && (

                        <Typography sx={{ fontSize: "12px", fontWeight: "bold" }}>
                            {message?.senderParticipantName} {timeString}
                        </Typography>
                    )}
                    {message?.body?.elementText?.textFormat === 'PLAINTEXT' ? (
                        <Typography>
                            <Linkify>
                                {message?.body?.elementText?.text.split('\n').map((line, i) => (
                                    <React.Fragment key={`${line}-${i}`}>
                                        {line}
                                        <br />
                                    </React.Fragment>
                                ))}
                            </Linkify>
                        </Typography>

                    ) : (
                        <Typography dangerouslySetInnerHTML={{ __html: message?.body?.elementText?.text }} />
                    )}
                    {isUserMessage && (
                        <Typography sx={{ fontSize: "10px", color: "#ccc", marginTop: "4px" }}>{timeString}</Typography>
                    )}
                </Box>
            )}

            {isChip ? (
                <Box sx={{
                    display: "flex",
                    flexFlow: "column",
                    width: "auto",
                    textAlign: "left",
                    background: "#e5e5ea",
                    padding: "12px",
                    borderRadius: "12px",
                    margin: "12px 12px 0 30px",
                    maxWidth: "fit-content"
                }}>
                    {actionList?.map((button: { text: string; payload: string }) => (
                    <PostbackButton
                        key={button.payload} 
                        isReplied={replied}
                        title={button.text}
                        payload={button.payload}
                        onPostback={handlePostback}
                        theme="dafabet"
                        messageId={message.messageId}
                        disabled={replied}
                    />
                    ))}
                </Box>
            ) : null}
            {isImage ? <RichMediaUpload payload={actionList}></RichMediaUpload> : null}
            {isDOB ? <DOBInputSeparate/> : null}
            {isInputText? <UsernameInput/>: null}
        </Box>
    );
}
